<template>
  <div>
    <div class="wz-page-header">
      <!-- Mismo patrón que ContractList: wz-page-header es flex-column, por eso anidamos un flex-row
           para poner el título a la izquierda y el panel de columnas a la derecha, en la misma fila. -->
      <div class="flex items-center justify-between">
        <h1 class="wz-page-title mb-0">Descansos médicos</h1>
        <!-- Tooltip interno suprimido (tooltip-text="") + wz-tooltip envolvente a la izquierda.
             Botón con su variant default primary-soft (sin overrides). -->
        <wz-tooltip text="Configurar columnas" position="left">
          <wz-columns-preferences-panel
            tooltip-text=""
            :columns="columns"
            :hidden-columns="hiddenColumns"
            @toggle="toggleColumn"
            @reorder="reorderColumns"
            @reset="resetColumns"
          />
        </wz-tooltip>
      </div>
    </div>

    <wz-table-container>

      <wz-table-toolbar>
        <template #left>
          <wz-filter-menu
            :options="filterOptions"
            :selected-option-filter="filters"
            @setFilters="setFilters"
          />
        </template>
        <template #right>
          <wz-button v-if="selectedIds.length > 0" variant="danger" @click="openBatchAnnul()">
            <i class="ri-close-circle-line mr-1"></i>{{ selectedIds.length > 1 ? `Anular ${selectedIds.length} seleccionados` : 'Anular' }}
          </wz-button>
          <wz-tooltip text="Recargar">
            <wz-button variant="outline" @click="loadData">
              <i class="ri-loop-right-line"></i>
            </wz-button>
          </wz-tooltip>
          <wz-tooltip text="Descargar">
            <wz-button variant="outline" @click="downloadReport">
              <i class="ri-download-2-line"></i>
            </wz-button>
          </wz-tooltip>
          <wz-search-bar
            placeholder="Buscar por nombre o número de documento"
            @search="onSearch"
            @input="search = $event"
          />
          <wz-button variant="outline" @click="$router.push({ name: 'medical-leave-bulk-upload' })">
            <i class="ri-upload-2-line mr-1"></i>Cargar
          </wz-button>
          <wz-button variant="primary" @click="$router.push({ name: 'medical-leave-create' })">Agregar</wz-button>
        </template>
        <template #filters v-if="showFiltersList">
          <wz-badge
            v-for="(val, name) in filterList"
            :key="name"
            variant="primary"
            pill
            dismissible
            @dismiss="deleteFilter(name)"
          >
            {{ getFilterName(name, val) }}
          </wz-badge>
        </template>
      </wz-table-toolbar>

      <wz-table>
        <thead>
          <tr>
            <th style="width: 36px; text-align: center;">
              <input type="checkbox" :checked="allSelected" @change="toggleSelectAll" title="Seleccionar todos" />
            </th>
            <th
              v-for="col in visibleColumns"
              :key="col.key"
              :class="[{
                'wz-th-sortable': col.sortKey,
                'is-sorted': col.sortKey && sortKey === col.sortKey,
              }]"
              :style="colStyle(col)"
              @click="col.sortKey ? toggleSort(col.sortKey) : null"
            >
              {{ col.label }}
              <i v-if="col.sortKey" :class="['wz-th-sort-icon', sortIcon(col.sortKey)]"></i>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="sortedList(list).length === 0">
            <td :colspan="visibleColumns.length + 1" class="text-center text-muted py-4">No se encontraron descansos médicos</td>
          </tr>
          <tr v-for="item in sortedList(list)" :key="item.id">
            <td style="text-align: center;">
              <input type="checkbox" :value="item.id" v-model="selectedIds" />
            </td>
            <td v-for="col in visibleColumns" :key="col.key" :style="colStyle(col)">
              <template v-if="col.key === 'employeeCode'">{{ item.employeeCode || '-' }}</template>
              <template v-else-if="col.key === 'employeeName'">{{ item.employeeName }}</template>
              <template v-else-if="col.key === 'companyName'">{{ item.companyName }}</template>
              <template v-else-if="col.key === 'leaveType'">
                <wz-badge :variant="badgeVariant(getTypeColor(item.leaveType))" pill>
                  {{ getTypeLabel(item.leaveType) }}
                </wz-badge>
              </template>
              <template v-else-if="col.key === 'startDate'"><span v-local-date="item.startDate"></span></template>
              <template v-else-if="col.key === 'endDate'"><span v-local-date="item.endDate"></span></template>
              <template v-else-if="col.key === 'calendarDays'">{{ item.calendarDays }}</template>
              <template v-else-if="col.key === 'totalSubsidyDaysYear'">{{ item.totalSubsidyDaysYear != null ? item.totalSubsidyDaysYear : '-' }}</template>
              <template v-else-if="col.key === 'status'">
                <wz-badge :variant="badgeVariant(getStatusColor(item.status))" pill>{{ item.status }}</wz-badge>
              </template>
              <template v-else-if="col.key === 'caseStatus'">
                <wz-badge :variant="badgeVariant(getCaseStatusColor(item.caseStatus))" pill>
                  {{ getCaseStatusLabel(item.caseStatus) }}
                </wz-badge>
              </template>
              <template v-else-if="col.key === 'processStatus'">
                <template v-if="item.caseStatus === 'SUBSIDIO' && item.processStatus">
                  <wz-badge :variant="badgeVariant(getProcessStatusColor(item.processStatus))" pill>
                    {{ getProcessStatusLabel(item.processStatus) }}
                  </wz-badge>
                </template>
                <template v-else>-</template>
              </template>
              <template v-else-if="col.key === 'amountPaid'">{{ formatCurrency(item.amountPaid) }}</template>
              <template v-else-if="col.key === 'personInCharge'">{{ item.personInCharge || '-' }}</template>
              <template v-else-if="col.key === 'options'">
                <wz-dropdown
                  :items="leaveActions(item)"
                  placement="left"
                  @click="handleLeaveAction(item, $event)"
                />
              </template>
            </td>
          </tr>
        </tbody>
      </wz-table>

      <wz-table-footer>
        <span>{{ list.length }} registro(s)</span>
      </wz-table-footer>

    </wz-table-container>

    <!-- Modal: Finalizar -->
    <wz-modal v-model="showFinalizeModal" title="Finalizar descanso médico" size="sm" :close-on-overlay="false">
      <div class="mb-3">
        <wz-datepicker label="Fecha real de fin" v-model="finalizeDate" required />
      </div>
      <div v-if="selectedItem && finalizeDate" class="text-muted text-sm mt-1">
        Días: {{ computeFinalizeDays() }}
      </div>
      <template #footer>
        <wz-button variant="outline" @click="showFinalizeModal = false">Cancelar</wz-button>
        <wz-button variant="primary" :disabled="!finalizeDate" @click="confirmFinalize">Finalizar</wz-button>
      </template>
    </wz-modal>

    <!-- Modal: Cancelar -->
    <wz-modal v-model="showCancelModal" title="Cancelar descanso médico" size="sm" :close-on-overlay="false">
      <wz-textarea
        v-model="cancelReason"
        label="Motivo de cancelación"
        placeholder="Ingrese el motivo de la cancelación"
        :rows="3"
        required
      />
      <template #footer>
        <wz-button variant="outline" @click="showCancelModal = false">Volver</wz-button>
        <wz-button variant="danger" :disabled="!cancelReason.trim()" @click="confirmCancel">Cancelar descanso</wz-button>
      </template>
    </wz-modal>

    <annul-by-mistake-modal
      v-model="showAnnul"
      :submitting="annulSubmitting"
      :entity-description="annulTarget ? `${annulTarget.employeeName} — DM del ${annulTarget.startDate} al ${annulTarget.endDate}` : ''"
      @confirm="doAnnul"
    />

    <annul-by-mistake-modal
      v-model="showBatchAnnul"
      :submitting="batchSubmitting"
      :entity-descriptions="batchDescriptions"
      @confirm="doBatchAnnul"
    />
  </div>
</template>

<script>
import { Alert } from "@wizepe/wizeui"
import { FilterMixin } from "@wizepe/wizeui"
import { SortMixin } from '@wizepe/wizeui'
import { draggableColumnsMixin } from '@wizepe/wizeui'
import { MedicalLeaveService, CompanyService } from '@/core/services'
import ColumnPreferencesService from '@/core/services/ColumnPreferencesService'
import AnnulByMistakeModal from '@/core/components/AnnulByMistakeModal.vue'
import { Constants as LocalConstants } from '@/core/utils'

// Orden por defecto de las columnas; el mixin aplica el orden guardado en localStorage.
// `options` es la única fija (queda siempre al final, no se arrastra).
function defaultColumns() {
  return [
    { key: 'employeeCode',         label: 'Código',            sortKey: 'employeeCode', fixed: false },
    { key: 'employeeName',         label: 'Colaborador',       sortKey: 'employeeName', fixed: false },
    { key: 'companyName',          label: 'Empresa',           fixed: false },
    { key: 'leaveType',            label: 'Tipo',              fixed: false },
    { key: 'startDate',            label: 'Fecha inicio',      sortKey: 'startDate', fixed: false },
    { key: 'endDate',              label: 'Fecha fin',         fixed: false },
    { key: 'calendarDays',         label: 'Días',              fixed: false, align: 'right' },
    { key: 'totalSubsidyDaysYear', label: 'Días subsidio año', fixed: false, align: 'right' },
    { key: 'status',               label: 'Estado',            fixed: false },
    { key: 'caseStatus',           label: 'Caso',              fixed: false },
    { key: 'processStatus',        label: 'Trámite',           fixed: false },
    { key: 'amountPaid',           label: 'Monto pagado',      fixed: false, align: 'right' },
    { key: 'personInCharge',       label: 'Encargado',         fixed: false },
    { key: 'options',              label: 'Opciones',          fixed: true, align: 'center' },
  ]
}

export default {
  name: 'MedicalLeaveList',
  components: { AnnulByMistakeModal },
  mixins: [SortMixin, FilterMixin, draggableColumnsMixin],
  props: {
    // Servicio de preferencias por usuario (backend). Override del prop del mixin con default,
    // ya que esta página no recibe props de un padre.
    preferencesService: { type: Object, default: () => ColumnPreferencesService },
  },
  data() {
    return {
      list: [],
      search: null,
      columnsStorageKey: 'crystal_medical_leave_columns_order',
      columnsListKey: 'medical_leaves',
      selectedItem:      null,
      finalizeDate:      null,
      cancelReason:      '',
      showFinalizeModal: false,
      showCancelModal:   false,
      showAnnul:         false,
      annulTarget:       null,
      annulSubmitting:   false,
      selectedIds:       [],
      showBatchAnnul:    false,
      batchSubmitting:   false,
      filterOptions: [
        { label: 'Empresa',           key: 'companyId',     type: LocalConstants.TypeFilters.SINGLE, options: [] },
        { label: 'Tipo',              key: 'leaveType',     type: LocalConstants.TypeFilters.SINGLE, options: Object.values(LocalConstants.MedicalLeaveType).map(t => ({ key: t.key, label: t.label })) },
        { label: 'Estado',            key: 'status',        type: LocalConstants.TypeFilters.SINGLE, options: Object.values(LocalConstants.MedicalLeaveStatus).map(s => ({ key: s.key, label: s.label })) },
        { label: 'Estado del caso',   key: 'caseStatus',    type: LocalConstants.TypeFilters.SINGLE, options: Object.values(LocalConstants.MedicalLeaveCaseStatus).map(s => ({ key: s.key, label: s.label })) },
        { label: 'Estado del trámite',key: 'processStatus', type: LocalConstants.TypeFilters.SINGLE, options: Object.values(LocalConstants.MedicalLeaveProcessStatus).map(s => ({ key: s.key, label: s.label })) },
        { label: 'Fecha de inicio',   key: 'startDate',     type: LocalConstants.TypeFilters.DATE, isRange: true },
      ],
    }
  },
  computed: {
    allSelected() {
      return this.list.length > 0 && this.list.every(i => this.selectedIds.includes(i.id))
    },
    batchDescriptions() {
      return this.list
        .filter(i => this.selectedIds.includes(i.id))
        .map(i => `${i.employeeName} — DM del ${i.startDate} al ${i.endDate}`)
    },
  },
  methods: {
    toggleSelectAll(e) {
      this.selectedIds = e.target.checked ? this.list.map(i => i.id) : []
    },
    openBatchAnnul() {
      if (this.selectedIds.length > 0) this.showBatchAnnul = true
    },
    async doBatchAnnul(reason) {
      try {
        this.batchSubmitting = true
        const resp = await MedicalLeaveService.annulBatch(this.selectedIds, reason)
        const payload = resp.payload || resp
        const ok = (payload.succeeded || []).length
        const failed = payload.failed || []
        if (failed.length === 0) {
          Alert.success(`${ok} descanso(s) médico(s) anulado(s) correctamente`)
        } else {
          const detail = failed.map(f => `#${f.id}: ${f.error}`).join('\n')
          Alert.alert('Advertencia', `${ok} anulado(s), ${failed.length} fallaron.\n${detail}`)
        }
        this.showBatchAnnul = false
        this.selectedIds = []
        this.loadData()
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al anular las selecciones')
      } finally {
        this.batchSubmitting = false
      }
    },
    // El label de los badges de filtro ahora lo genera getFilterName del FilterMixin compartido
    // (@wizepe/wizeui >= 0.1.189): cubre SINGLE (`label = valor`) y DATE isRange
    // (`label: dd/MM/yyyy - dd/MM/yyyy`, con variantes desde/hasta y rango vacío sin badge).
    badgeVariant(value) { return value || 'dark' },
    // Alineación por columna (se aplica en <th> y <td>); el drag/persistencia vienen de draggableColumnsMixin.
    colStyle(col) {
      return col.align ? `text-align: ${col.align};` : ''
    },
    leaveActions(item) {
      const actions = [{ key: 'detail', label: 'Ver detalle' }]
      if (item.status === 'ACTIVO') {
        actions.push({ key: 'edit',     label: 'Editar' })
        actions.push({ key: 'finalize', label: 'Finalizar' })
        actions.push({ key: 'cancel',   label: 'Cancelar', danger: true })
      }
      // Anular por error: administrativa, disponible salvo si ya está cancelado/anulado (permite FINALIZADO).
      if (item.status !== 'CANCELADO') {
        actions.push({ key: 'annul', label: 'Anular', danger: true })
      }
      return actions
    },
    handleLeaveAction(item, action) {
      if (action.key === 'detail')   this.$router.push({ name: 'medical-leave-detail', params: { id: item.id } })
      if (action.key === 'edit')     this.$router.push({ name: 'medical-leave-edit', params: { id: item.id } })
      if (action.key === 'finalize') this.openFinalize(item)
      if (action.key === 'cancel')   this.openCancel(item)
      if (action.key === 'annul')    this.openAnnul(item)
    },
    openAnnul(item) { this.annulTarget = item; this.showAnnul = true },
    async doAnnul(reason) {
      try {
        this.annulSubmitting = true
        await MedicalLeaveService.annulMedicalLeave(this.annulTarget.id, reason)
        Alert.success('Descanso médico anulado por error')
        this.showAnnul = false
        this.loadData()
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al anular el descanso médico')
      } finally {
        this.annulSubmitting = false
      }
    },
    buildFilterParams() {
      const params = {}
      if (this.filters.companyId)     params.companyId     = this.filters.companyId
      if (this.filters.leaveType)     params.leaveType     = this.filters.leaveType
      if (this.filters.status)        params.status        = this.filters.status
      if (this.filters.caseStatus)    params.caseStatus    = this.filters.caseStatus
      if (this.filters.processStatus) params.processStatus = this.filters.processStatus
      // Rango de fecha de inicio: WzFilterMenu ya entrega { start, end } en ISO (sin re-formatear acá).
      // Se aplana a los 2 params ISO que espera el backend (startDateFrom / startDateTo).
      const range = this.filters.startDate
      if (range && typeof range === 'object') {
        if (range.start) params.startDateFrom = range.start
        if (range.end)   params.startDateTo   = range.end
      }
      if (this.search)                params.search        = this.search
      return params
    },
    // Búsqueda libre (nombre completo o DNI); el debounce lo maneja wz-search-bar.
    onSearch(keyword) {
      this.search = keyword
      this.loadData()
    },
    // Descarta el orden de columnas guardado si es previo a la columna "Código", para forzar su
    // posición por defecto. No toca el mixin (solo esta clave de localStorage), así que no afecta
    // a otras listas que compartan draggableColumnsMixin.
    migrateColumnsForEmployeeCode() {
      try {
        const raw = window.localStorage.getItem(this.columnsStorageKey)
        if (!raw) return
        const order = JSON.parse(raw)
        if (Array.isArray(order) && !order.includes('employeeCode')) {
          window.localStorage.removeItem(this.columnsStorageKey)
        }
      } catch (e) {
        // Orden guardado corrupto: lo descartamos; initColumns reconstruye desde el default.
        window.localStorage.removeItem(this.columnsStorageKey)
      }
    },
    async loadData() {
      try {
        this.$store.dispatch('app/loading', true)
        const resp = await MedicalLeaveService.getAll(this.buildFilterParams())
        this.list = resp.payload || []
        this.selectedIds = []
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al cargar descansos médicos')
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
    async downloadReport() {
      try {
        this.$store.dispatch('app/loading', true)
        const resp = await MedicalLeaveService.downloadXlsx(this.buildFilterParams())
        const today = new Date().toISOString().split('T')[0]
        const url = window.URL.createObjectURL(resp.data)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `descansos-medicos-${today}.xlsx`)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
      } catch (e) {
        Alert.error('Error al descargar el reporte')
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
    getTypeColor(leaveType) {
      const found = Object.values(LocalConstants.MedicalLeaveType).find(t => t.key === leaveType)
      return found ? found.color : 'dark'
    },
    getTypeLabel(leaveType) {
      const found = Object.values(LocalConstants.MedicalLeaveType).find(t => t.key === leaveType)
      return found ? found.label : leaveType
    },
    getStatusColor(status) {
      const found = Object.values(LocalConstants.MedicalLeaveStatus).find(s => s.key === status)
      return found ? found.color : 'dark'
    },
    getCaseStatusColor(caseStatus) {
      const found = Object.values(LocalConstants.MedicalLeaveCaseStatus).find(s => s.key === caseStatus)
      return found ? found.color : 'dark'
    },
    getCaseStatusLabel(caseStatus) {
      const found = Object.values(LocalConstants.MedicalLeaveCaseStatus).find(s => s.key === caseStatus)
      return found ? found.label : (caseStatus || '-')
    },
    getProcessStatusColor(processStatus) {
      const found = Object.values(LocalConstants.MedicalLeaveProcessStatus).find(s => s.key === processStatus)
      return found ? found.color : 'dark'
    },
    getProcessStatusLabel(processStatus) {
      const found = Object.values(LocalConstants.MedicalLeaveProcessStatus).find(s => s.key === processStatus)
      return found ? found.label : (processStatus || '-')
    },
    formatCurrency(amount) {
      if (amount == null) return '-'
      return `S/ ${Number(amount).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    },
    openFinalize(item) {
      this.selectedItem = item
      this.finalizeDate = null
      this.showFinalizeModal = true
    },
    openCancel(item) {
      this.selectedItem = item
      this.cancelReason = ''
      this.showCancelModal = true
    },
    computeFinalizeDays() {
      if (!this.selectedItem || !this.finalizeDate) return '-'
      const start = new Date(this.selectedItem.startDate + 'T00:00:00')
      const end   = new Date(this.finalizeDate          + 'T00:00:00')
      return Math.round((end - start) / (1000 * 60 * 60 * 24)) + 1
    },
    async confirmFinalize() {
      try {
        this.$store.dispatch('app/loading', true)
        await MedicalLeaveService.finalize(this.selectedItem.id, { actualEndDate: this.finalizeDate })
        this.showFinalizeModal = false
        Alert.success('Descanso médico finalizado correctamente')
        this.loadData()
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al finalizar')
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
    async confirmCancel() {
      try {
        this.$store.dispatch('app/loading', true)
        await MedicalLeaveService.cancel(this.selectedItem.id, { cancellationReason: this.cancelReason })
        this.showCancelModal = false
        Alert.success('Descanso médico cancelado')
        this.loadData()
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al cancelar')
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
  },
  async created() {
    // Migración one-shot: si hay un orden de columnas guardado ANTERIOR a la columna "Código"
    // (no incluye la key 'employeeCode'), se descarta para que la nueva columna aparezca en su
    // posición por defecto (primera) en vez de al final. Solo afecta a esta lista (clave propia).
    this.migrateColumnsForEmployeeCode()
    this.initColumns(defaultColumns(), this.columnsStorageKey, this.columnsListKey)
    try {
      const resp = await CompanyService.listAll()
      this.filterOptions[0].options = (resp.payload || []).map(c => ({ key: c.id, label: c.businessName || c.name }))
    } catch (e) { console.error(e) }
    this.loadData()
  },
}
</script>

<style lang="stylus">
.col-draggable
  cursor move
</style>
